import { axiosInstance } from "utils/AxiosInstance";

export interface User {
  id: string;
  name: string;
  email: string;
  organizationId: string;
  organizationName: string;
  role: string;
  status: 'member' | 'invite';
  updatedAt: string;
}

export interface UsersAndInvitesResponse {
  data: User[];
  paging: {
    total: number;
    totalUsers: number;
    totalInvites: number;
    page: number;
    pageSize: number;
    sortBy: string;
    sortOrder: string;
  };
}

export interface Organization {
  id: string;
  name: string;
}

export interface UserStats {
  totalUsers: number;
  totalOrganizations: number;
  totalRevisions: number;
  activeUsers: number;
  averageConnectionTime: number;
}

export interface GetUsersResponse {
  data: User[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

export interface UsersResponse {
  data: User[];
  paging: {
    total: number;
    page: number;
    pageSize: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
  };
}

export const getUserStats = async (): Promise<UserStats> => {
  const response = await axiosInstance.get('/admin/users/stats');
  return response.data;
};

export const getOrganizationsNames = async (): Promise<{ id: string; name: string }[]> => {
  const response = await axiosInstance.get('/admin/organization/names');
  return response.data;
};


export const getOrganizationMembers = async ({
  page,
  pageSize,
  sortOrder,
  sortBy,
  searchOrganization,
  searchType,
  searchEmail
}: {
  page?: number,
  pageSize?: number,
  sortOrder?: 'asc' | 'desc',
  sortBy?: string,
  searchOrganization?: string,
  searchType?: string,
  searchEmail?: string
}): Promise<UsersResponse> => {
  const response = await axiosInstance.post('/analytics/members', {
    page,
    pageSize,
    sortOrder,
    sortBy,
    searchOrganization,
    searchType,
    searchEmail
  });
  return response.data;
};

export const getOrganizationInvites = async ({
  page,
  pageSize,
  sortOrder,
  sortBy,
  searchOrganization,
  searchType,
  searchEmail
}: {
  page: number;
  pageSize: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  searchOrganization: string;
  searchType: 'all' | 'email' | 'organization';
  searchEmail: string;
}): Promise<UsersResponse> => {
  const response = await axiosInstance.post('/analytics/invites', {
    page,
    pageSize,
    sortOrder,
    sortBy,
    searchOrganization,
    searchType, 
    searchEmail
  });
  return response.data;
};

export const toggleMaintenanceMode = async (enabled: boolean) => {
  const response = await axiosInstance.post('/admin/maintenance', { enabled });
  return response.data;
};

export const getMaintenanceStatus = async () => {
  const response = await axiosInstance.get('/admin/maintenance');
  return response.data;
};

export const getOrganizationMembersStats = async (organizationId: string) => {
  const response = await axiosInstance.post(`/analytics/members/stats`, { organizationId });
  return response.data;
};

export const downloadOrganizationMembersCSV = async ({
  sortOrder,
  sortBy,
  searchOrganization,
  searchType,
  searchEmail
}: {
  sortOrder?: 'asc' | 'desc',
  sortBy?: string,
  searchOrganization?: string,
  searchType?: string,
  searchEmail?: string
}): Promise<Blob> => {
  const response = await axiosInstance.post('/analytics/members/export-csv', {
    sortOrder,
    sortBy,
    searchOrganization,
    searchType,
    searchEmail
  }, {
    responseType: 'blob'
  });
  return response.data;
};

export const downloadOrganizationInvitesCSV = async ({
  sortOrder,
  sortBy,
  searchOrganization,
  searchType,
  searchEmail
}: {
  sortOrder?: 'asc' | 'desc',
  sortBy?: string,
  searchOrganization?: string,
  searchType?: string,
  searchEmail?: string
}): Promise<Blob> => {
  const response = await axiosInstance.post('/analytics/invites/export-csv', {
    sortOrder,
    sortBy,
    searchOrganization,
    searchType,
    searchEmail
  }, {
    responseType: 'blob'
  });
  return response.data;
};

